import React, { useState, useEffect } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { t } from 'i18next';

interface CountdownProps {
  targetTime: Dayjs;
  onDue?: () => void; // 新增的回调函数
}

const CountdownTimer: React.FC<CountdownProps> = ({ targetTime, onDue }) => {
  const [timeLeft, setTimeLeft] = useState(calculateTimeLeft(targetTime));
  const [hasTriggeredDue, setHasTriggeredDue] = useState(false); // 防止重复触发

  useEffect(() => {
    const timer = setInterval(() => {
      const newTimeLeft = calculateTimeLeft(targetTime);
      setTimeLeft(newTimeLeft);

      // 检查是否逾期且未触发过回调
      if (newTimeLeft.totalSeconds <= 0 && !hasTriggeredDue && onDue) {
        onDue();
        setHasTriggeredDue(true);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [targetTime, onDue, hasTriggeredDue]);

  function calculateTimeLeft(target: Dayjs) {
    const now = dayjs();
    const nextHour = target.add(1, 'hour').startOf('hour');
    const diff = nextHour.diff(now, 'second');

    if (diff <= 0) {
      return { days: 0, hours: 0, minutes: 0, seconds: 0, totalSeconds: 0 };
    }

    const days = Math.floor(diff / (3600 * 24));
    const hours = Math.floor((diff % (3600 * 24)) / 3600);
    const minutes = Math.floor((diff % 3600) / 60);
    const seconds = diff % 60;

    return { days, hours, minutes, seconds, totalSeconds: diff };
  }

  const getDisplayText = () => {
    const { days, hours, minutes, seconds } = timeLeft;

    if (days > 0) {
      return `${days}${t('requirement.day')}${hours}${t('requirement.hour')}`;
    } else if (timeLeft.totalSeconds >= 3600) {
      return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    } else {
      return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    }
  };

  const getTextColor = () => {
    if (timeLeft.totalSeconds <= 3600) {
      return 'red';
    } else if (timeLeft.totalSeconds <= 86400) {
      return 'orange';
    }
    return 'inherit';
  };

  return (
    <div style={{ color: getTextColor(), fontWeight: 'bold', wordBreak: 'keep-all' }}>
      {timeLeft.totalSeconds > 0 ? getDisplayText() : t('requirement.timedOut')}
    </div>
  );
};

export default CountdownTimer;